import { PaginatedData, PaginatedResponse } from "../../types";
import { APIQuery } from "../../utils/sharedTypes";
import { ApiServiceCollection } from "../ApiServiceCollection.class";
import { ApiServiceEndpoint, ApiServiceEndpointResponse } from "../api.types";
import {
    OrganizationAttribution,
    OrganizationAttributionSummary,
} from "../attribution/types";
import { MOCK_TIMEOUT_WAIT, deepJsonCopy, getMockedResponse } from "../utils";
import { Organization, OrganizationDB } from "./types";
import { sortConfigToQueryParam } from "../../utils/sortUtils";
import { ATTRIBUTION_SCHEMA_TYPES } from "../mocks/MockDatabaseCRUD";
import { ApiResponse } from "../RequestManager.class";
import { OrganizationListAPI } from "../mocks/database/tables/organizations.mockTable";
import { OrganizationAttributionAPI } from "../mocks/database/tables/organizations_attributions.mockTable";
import { MOCKED_ORGANIZATION_DEFAULTS } from "./mockData";
import { fixDoubleEscapedStringResponse } from "../../utils/fixDoubleEscapedStringResponse";

export class OrganizationService extends ApiServiceCollection {
    getOrganizationAttributions: ApiServiceEndpoint<PaginatedData<OrganizationAttributionSummary>> = ({
        organizationGuid,
        skip = 0,
        limit = 10,
        query
    }: { skip: number; limit: number, organizationGuid: string, query?: APIQuery }
    ) => {
        const queryParams = this.endpointParamsToQueryParams({ skip, limit, query });
        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL as string}/organization/${organizationGuid}/attribution?${queryParams}`, {
            method: 'GET',
            headers: {
                ...this.getJSONHeaders()
            },
        }, {
            mock: this.mocked,
            mockData: () => {
                try {
                    const mockDataResp = this.getMockData({
                        tableName: 'organizations_attributions',
                        skip, limit, sort: sortConfigToQueryParam(query?.sort || null) || undefined,
                        queryParams: {
                            ...(query?.filter || {}),
                            organizationGuid,
                            deleted: 0
                        },
                        paginated: true
                    });
                    const { data: paginatedResp, ...mockDataRem } = mockDataResp;
                    const { data: paginatedData, total } = paginatedResp;
                    return {
                        ...mockDataRem,
                        data: {
                            total,
                            data: paginatedData.filter((rec: Record<string, any>) => ATTRIBUTION_SCHEMA_TYPES.indexOf(rec['type']) > -1)
                        }
                    }
                } catch (e) {
                    return {
                        data: { error: `An unexpected error occurred` },
                        status: 500,
                        error: {
                            message: `An unexpected error occurred`,
                            code: 'INTERNAL_SERVER'
                        }
                    }
                }
            },
            getMockStatus: () => this.getNextMockStatus('OrganizationService.getOrganizationAttributions'),
            transformResponsePayload: async (apiResponse: ApiResponse) => {
                const response = apiResponse.response;
                const respData = await response?.json() as PaginatedResponse<OrganizationAttributionAPI>;
                const total = respData.total;
                const attributions = (respData.attributions || respData.data || []).map(attr => ({
                    attributionGuid: attr.attribution_guid,
                    displayName: attr.display_name,
                    status: attr.status,
                    type: attr.type,
                    organizationGuid: organizationGuid,
                    required: attr.required,
                    organizationAttributionGuid: attr.organization_attribution_guid
                })) as OrganizationAttributionSummary[];

                return {
                    response: response as Response,
                    success: true,
                    jsonBody: {
                        data: attributions,
                        total
                    }
                };
            }
        })
        .catch(error => error);
    }

    private _translateOrgStatusFromServer(status: boolean | number | undefined) {
        if (status === undefined) {
            return 'Active';
        }
        return status ? 'Active' : 'Inactive';
    }

    private _translateOrgStatusToServer(status: string | undefined) {
        if (status === undefined) {
            return true;
        }
        return status === 'Active';
    }

    getOrganizationById: ApiServiceEndpoint<Organization> = (organizationGuid: string) => {
        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL as string}/organization/${organizationGuid}`, {
            method: 'GET',
            headers: {
                ...this.getJSONHeaders()
            },
        }, {
            mock: this.mocked,
            mockData: this.getMockDataSingular({
                tableName: 'organizations',
                queryParams: { organization_guid: organizationGuid }
            }),
            getMockStatus: () => this.getNextMockStatus('OrganizationService.getOrganizationById'),
            transformResponsePayload: async (apiResponse: ApiResponse) => {
                const response = apiResponse.response;
                const { display_name, organization_guid, metadata_schema_type, machine_name, ...remaining } = await response?.json() as OrganizationDB;
                return {
                    response: response as Response,
                    success: true,
                    jsonBody: {
                        organizationGuid: organization_guid,
                        displayName: display_name,
                        machineName: machine_name,
                        metadataSchemaType: metadata_schema_type || '',
                        status: this._translateOrgStatusFromServer(remaining.is_active),
                        oktaGroupId: remaining.okta_group_id,
                        oktaApplicationId: remaining.okta_application_id,
                        clientId: remaining.client_id,
                        clientSecret: remaining.client_secret,

                    } as Organization
                }
            }
        })
            .catch(error => error);
    }

    getOrganizations: ApiServiceEndpoint<PaginatedData<Organization>> = (
        params: { skip: number; limit: number, query?: APIQuery }
    ) => {
        // let response: Response | null = null;
        const queryParams = this.endpointParamsToQueryParams(params)

        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL as string}/organization?${queryParams}`, {
            method: 'GET',
            headers: {
                ...this.getJSONHeaders()
            },
        }, {
            mock: this.mocked,
            mockData: this.getMockData({
                tableName: 'organizations',
                queryParams,
                paginated: true
            }),
            getMockStatus: () => this.getNextMockStatus('OrganizationService.getOrganizations'),
            transformResponsePayload: async (apiResponse: ApiResponse) => {
                const response = apiResponse.response;
                const respData = await response?.json() as PaginatedResponse<OrganizationListAPI>;
                return {
                    response: response as Response,
                    success: true,
                    jsonBody: {
                        ...respData,
                        data: (respData.organizations || respData.data || []).map(({
                            display_name,
                            machine_name,
                            organization_guid,
                            is_active,
                            ...remaining
                        }) => ({
                            organizationGuid: organization_guid,
                            displayName: display_name,
                            machineName: machine_name,
                            status: this._translateOrgStatusFromServer(is_active),
                            oktaGroupId: remaining.okta_group_id,
                            oktaApplicationId: remaining.okta_application_id
                        } as Organization))
                    }
                }
            }
        })
            .catch(error => error);
    }

    getOrganizationDefaults: ApiServiceEndpoint<Organization> = (status: number = 200) => {
        return new Promise<ApiServiceEndpointResponse<Organization>>((resolve, reject) => {
            setTimeout(() => {

                if (!this.mockedStatusShouldProceed('OrganizationService.getOrganizationDefaults', status)) {
                    reject();
                    return
                }

                const respData: OrganizationDB = deepJsonCopy(MOCKED_ORGANIZATION_DEFAULTS);
                resolve({
                    response: getMockedResponse(respData, status),
                    success: true,
                    jsonBody: {
                        organizationGuid: respData.organization_guid || '',
                        displayName: respData.display_name,
                        machineName: respData.machine_name || respData.display_name,
                        status: this._translateOrgStatusFromServer(respData.is_active),
                        oktaGroupId: respData.okta_group_id,
                        oktaApplicationId: respData.okta_application_id,
                        metadataSchemaType: respData.metadata_schema_type
                    }
                });
            }, MOCK_TIMEOUT_WAIT);
        });
    }

    createOrganization: ApiServiceEndpoint<string> = (data: Omit<
        Organization,
        "organizationGuid" | "oktaGroupId" | "oktaApplicationId"
    >) => {
        let response: Response | null = null;
        const reqBody = {
            "display_name": data.displayName,
            "is_active": this._translateOrgStatusToServer(data.status),
            "machine_name": data.machineName,
            "metadata_schema_type": data.metadataSchemaType
        };
        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL as string}/organization`, {
            method: 'POST',
            headers: {
                ...this.getJSONHeaders()
            },
            body: JSON.stringify(reqBody)
        }, {
            mock: this.mocked,
            mockData: () => {
                try {
                    const record = this.mockCRUD!.db().getTableByName('organizations').createRecord(reqBody);
                    return {
                        data: record.values['organization_id'],
                        status: 200
                    }
                } catch (e) {
                    if (e instanceof Error && e.message.includes('already exists')) {
                        return {
                            data: { error: e.message },
                            status: 409,
                            error: {
                                message: e.message,
                                code: 'CONFLICT'
                            }
                        }
                    }
                    return {
                        data: { error: `An unexpected error occurred` },
                        status: 500,
                        error: {
                            message: `An unexpected error occurred`,
                            code: 'INTERNAL_SERVER'
                        }
                    }
                }
            },
            getMockStatus: () => this.getNextMockStatus('OrganizationService.createOrganization')
        })
            .then(resp => {
                response = resp.response!;
                return response.text()
            })
            .then(respData => {
                return {
                    response: response as Response,
                    success: true,
                    textBody: fixDoubleEscapedStringResponse(respData)
                }
            })
            .catch(error => error);
    }

    updateOrganization: ApiServiceEndpoint<string> = (data: Omit<
        Organization,
        "organizationGuid" | "oktaGroupId" | "oktaApplicationId"
    >, organizationGuid: string) => {
        const requestData: Record<string, any> = {
            display_name: data.displayName,
            machine_name: data.machineName,
            metadata_schema_type: data.metadataSchemaType,
            is_active: this._translateOrgStatusToServer(data.status),
        }
        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL as string}/organization/${organizationGuid}`, {
            method: 'PATCH',
            headers: {
                ...this.getJSONHeaders()
            },
            body: JSON.stringify(requestData)
        }, {
            mock: this.mocked,
            mockData: () => {
                try {
                    this.mockCRUD!.db().getTableByName('organizations').updateRecords([
                        {
                            fieldName: 'organization_guid',
                            value: organizationGuid
                        }
                    ], requestData);
                    return {
                        data: null,
                        status: 204
                    }
                } catch (e) {
                    if (e instanceof Error && e.message.includes('already exists')) {
                        return {
                            data: { error: e.message },
                            status: 409,
                            error: {
                                message: e.message,
                                code: 'CONFLICT'
                            }
                        }
                    }
                    return {
                        data: { error: `An unexpected error occurred` },
                        status: 500,
                        error: {
                            message: `An unexpected error occurred`,
                            code: 'INTERNAL_SERVER'
                        }
                    }
                }
            },
            getMockStatus: () => this.getNextMockStatus('OrganizationService.updateOrganization')
        })
            .then(resp => {
                return {
                    response: resp.response,
                    success: true,
                    jsonBody: organizationGuid
                }
            })
            .catch(error => error);
    }

    createOrganizationAttribution: ApiServiceEndpoint<string> = (data: Omit<OrganizationAttribution, 'organizationAttributionGuid'>) => {
        let response: Response | null = null;
        const requestBody = {
            attribution_guid: data.attributionGuid,
            organization_guid: data.organizationGuid,
            required: data.required,
        };
        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL as string}/organization/${data.organizationGuid}/attribution`, {
            method: 'POST',
            headers: {
                ...this.getJSONHeaders()
            },
            body: JSON.stringify(requestBody),
        }, {
            mock: this.mocked,
            mockData: () => {
                try {
                    const rec = this.mockCRUD!.db().getTableByName('organizations_attributions').createRecord(requestBody);
                    return {
                        data: rec.values['organization_attribution_guid'],
                        status: 200
                    }
                } catch (e) {
                    return {
                        data: { error: `An unexpected error occurred` },
                        status: 500,
                        error: {
                            message: `An unexpected error occurred`,
                            code: 'INTERNAL_SERVER'
                        }
                    }
                }
            },
            getMockStatus: () => this.getNextMockStatus('OrganizationService.createOrganizationAttribution')
        })
            .then(resp => {
                response = resp.response!;
                return response.text();
            })
            .then(resp => {
                return {
                    response,
                    success: true,
                    textBody: fixDoubleEscapedStringResponse(resp) as string
                };
            })
            .catch(error => error);
    }

    updateOrganizationAttribution: ApiServiceEndpoint<void> = (data: OrganizationAttribution) => {
        const reqBody = {
            required: data.required
        };
        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL}/organization/${data.organizationGuid}/attribution/${data.attributionGuid}`, {
            method: 'PATCH',
            headers: {
                ...this.getJSONHeaders()
            },
            body: JSON.stringify(reqBody)
        }, {
            mock: this.mocked,
            mockData: () => {
                try {
                    const orgAttrTable = this.mockCRUD!.db().getTableByName('organizations_attributions');
                    const orgAttRecs = orgAttrTable.query([
                        {
                            fieldName: 'organization_guid',
                            value: data.organizationGuid
                        },
                        {
                            fieldName: 'attribution_guid',
                            value: data.attributionGuid
                        },
                        {
                            fieldName: 'deleted',
                            value: 0
                        }
                    ]);
                    if (!orgAttRecs.length) {
                        console.error('Unable to update organization attribution. Unable to find existing record');
                        return {
                            data: { error: 'Unable to update organization attribution. Unable to find existing record' },
                            status: 500,
                            error: {
                                message: 'Unable to update organization attribution. Unable to find existing record',
                                code: 'BAD_DATA'
                            }
                        };
                    }
                    this.mockCRUD!.db().getTableByName('organizations_attributions').updateRecords([
                        {
                            fieldName: 'id',
                            value: orgAttRecs[0].values['id']
                        }
                    ], reqBody);
                    return {
                        data: null,
                        status: 204
                    }
                } catch (e) {
                    return {
                        data: { error: `An unexpected error occurred` },
                        status: 500,
                        error: {
                            message: `An unexpected error occurred`,
                            code: 'INTERNAL_SERVER'
                        }
                    }
                }
            },
            getMockStatus: () => this.getNextMockStatus('OrganizationService.updateOrganizationAttribution')
        })
            .then(resp => {
                // Server returns an empty body
                return {
                    response: resp.response!,
                    success: true
                };
            })
            .catch(error => error);
    }

    removeAttribution: ApiServiceEndpoint<void> = (data: { organizationGuid: string; attributionGuid: string }) => {
        const { organizationGuid, attributionGuid } = data;
        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL}/organization/${organizationGuid}/attribution/${attributionGuid}`, {
            method: 'DELETE',
            headers: {
                ...this.getJSONHeaders()
            },
        }, {
            mock: this.mocked,
            mockData: () => {
                try {
                    this.mockCRUD!.db().getTableByName('organizations_attributions').updateRecords([
                        {
                            fieldName: 'organization_guid',
                            value: data.organizationGuid
                        },
                        {
                            fieldName: 'attribution_guid',
                            value: data.attributionGuid
                        }
                    ], {
                        deleted: 1
                    });
                    return {
                        data: null,
                        status: 204
                    }
                } catch (e) {
                    return {
                        data: { error: `An unexpected error occurred` },
                        status: 500,
                        error: {
                            message: `An unexpected error occurred`,
                            code: 'INTERNAL_SERVER'
                        }
                    }
                }
            },
            getMockStatus: () => this.getNextMockStatus('OrganizationService.removeAttribution')
        })
            .then(resp => {
                return {
                    response: resp.response!,
                    success: true
                }; // Empty response body
            })
            .catch(error => error);
    }
}

